// Copyright 2024 Sorama B.V.

import { GenericMeasurement } from "datatypes/SoramaWebApi";
import { Point2 } from "libraries/DataTypes/Point2";
import { PsychoAcousticMetricsLayoutMap, SpiderChartFactory } from "../Layout/SpiderChartFactory";
import { SVGElementBuilder } from "../Layout/SVGElementBuilder";
import {
    AllPsychoAcousticMetricTypes,
    METRIC_ORDER_CLOCKWISE_FROM_TOP,
    PsychoAcousticAnalysisResult,
    PsychoAcousticMetricType,
} from "../SpiderChart";

/**
 * Manages the creation, rendering, and interaction of the Spider Chart SVG elements. The SpiderChartSVGManager class
 * is responsible for handling all aspects of the spider chart visualization within an SVG.
 */
export class SpiderChartSVGManager {
    // Unique id for placing the spider chart
    public svgPlacerId: string;

    // The SVG element containing the spider chart
    public svg: SVGElement;

    // Object containing the psychoacoustic metrics, their display names, and their points.
    // This is used to map values between 0 and 1 to the correct point on the spider chart
    public psychoAcousticMetricLayout: PsychoAcousticMetricsLayoutMap;

    constructor(svgPlacerId: string) {
        this.svgPlacerId = svgPlacerId;
        this.psychoAcousticMetricLayout = SpiderChartFactory.createSpiderChartMetricsLayout();
        this.svg = SpiderChartFactory.createSpiderChart(this.psychoAcousticMetricLayout, this.svgPlacerId);
    }

    //#region Spider Chart Management:

    /**
     * Closes the spider chart and removes it from the DOM.
     */
    public close() {
        if (this.svg) {
            document.getElementById(this.svgPlacerId)?.removeChild(this.svg);
        }
        this.svg = undefined;
    }

    /**
     * Checks if the spider chart has been created and exists in the DOM.
     * @returns {boolean} True if the chart exists, false otherwise.
     */
    public hasSpiderChartBeenCreated(): boolean {
        return this.svg && document.getElementById(this.svgPlacerId).contains(this.svg);
    }

    /**
     * Appends the SVG element to the DOM in the specified placer ID.
     */
    public appendSVGToDOM() {
        const placerElement = document.getElementById(this.svgPlacerId);
        if (placerElement) {
            placerElement.appendChild(this.svg);
        }
    }

    /**
     * Removes the SVG element from the DOM.
     */
    public removeSVGFromDOM() {
        const placerElement = document.getElementById(this.svgPlacerId);
        if (placerElement && this.svg) {
            placerElement.removeChild(this.svg);
        }
    }

    //#endregion

    //#region Measurement Management

    /**
     * Adds a measurement's polygon to the spider chart based on provided values.
     */
    public addMeasurementToSpiderChart(
        measurement: GenericMeasurement,
        values: PsychoAcousticAnalysisResult,
        color: string,
        hasStroke = true
    ): SVGElement {
        const measurementId = measurement.id;
        const points = this.getPointsFromMeasurement(values);
        const elementId = this.getMeasurementSVGElementId(measurementId);

        const orderedPoints = METRIC_ORDER_CLOCKWISE_FROM_TOP.map((metricType) => points[metricType]);

        const polygon = SpiderChartFactory.createPolygonFill(orderedPoints, color, elementId, hasStroke);
        this.svg.appendChild(polygon);

        return polygon;
    }

    /**
     * Removes a measurement's polygon from the spider chart by measurement ID.
     */
    public removeMeasurementFromSpiderChart(measurementId: string) {
        const elementId = this.getMeasurementSVGElementId(measurementId);
        const polygon = document.getElementById(elementId);
        if (polygon) {
            this.svg.removeChild(polygon);
        }
    }

    /**
     * Clears all measurement polygons from the spider chart.
     */
    public clearAllMeasurementsFromSpiderChart() {
        const SVGElements = Array.from(this.svg.children)
            .filter((e) => e instanceof SVGElement)
            .filter((e) => e.id.split("+").length === 2)
            .map((e) => e.id);

        for (const elementId of SVGElements) {
            const polygon = document.getElementById(elementId);
            if (polygon) {
                this.svg.removeChild(polygon);
            }
        }
    }

    /**
     * Changes the fill and stroke color of a measurement polygon in the spider chart.
     */
    public changeMeasurementColor(measurementId: string, color: string) {
        const elementId = this.getMeasurementSVGElementId(measurementId);
        const polygon = document.getElementById(elementId);
        if (polygon) {
            polygon.setAttribute("fill", color);
            polygon.setAttribute("stroke", color);
        }
        this.displayMetricValues(undefined, color);
    }

    /**
     * Get the measurement IDs of all measurements in the spider chart
     * Assumes id is generated using getMeasurementSVGElementId()
     */
    public getAllMeasurementIdsInSpiderChart(): string[] {
        return this.getAllMeasurementElementIdsInSpiderChart().map((id) => id.split("+")[1]);
    }

    /**
     * Get all the measurement HTML element IDs in the spider chart
     * @returns all the measurement HTML element IDs in the spider chart
     */
    public getAllMeasurementElementIdsInSpiderChart(): string[] {
        return Array.from(this.svg.children)
            .filter((e) => e instanceof SVGElement)
            .filter((e) => e.id && e.id.includes(this.svgPlacerId)) // Filter for measurement elements
            .filter((e) => e.id.split("+").length === 2)
            .map((e) => e.id);
    }

    /**
     * Generates the unique deterministic ID for the SVG element representing the measurement.
     * Intentional duplication of the method in SpiderChart.
     */
    public getMeasurementSVGElementId(measurementId: string): string {
        // Prefix the measurement ID to the svgPlacer Id to support multiple spider charts with the same measurement
        return this.svgPlacerId + "+" + measurementId;
    }

    /**
     * Generates the unique deterministic ID for the DIV element representing the measurement.
     * Intentional duplication of the method in SpiderChart.
     */
    public getMeasurementDivElementId(measurementId: string): string {
        // Suffix the measurement ID to the svgPlacer Id support multiple spider charts with the same measurement
        return measurementId + "=" + this.svgPlacerId;
    }

    /**
     * Generates the unique deterministic ID for the text element representing the metric.
     */
    public getTextElementId(unit: string): string {
        return this.svgPlacerId + "=" + unit;
    }

    //#endregion

    //#region Rendering and Layout

    /**
     * Generates the SVG element for a polygon based on a set of points.
     */
    private getPointsFromMeasurement(values: PsychoAcousticAnalysisResult): Record<PsychoAcousticMetricType, Point2> {
        const metricPoints: Record<PsychoAcousticMetricType, Point2> = {} as Record<PsychoAcousticMetricType, Point2>;
        for (const metricType of AllPsychoAcousticMetricTypes) {
            let value = values[metricType];
            if (isNaN(value) || value === undefined) {
                value = 0;
            }
            metricPoints[metricType] = this.getMetricPoint(value, metricType);
        }

        return metricPoints;
    }

    /**
     * Given a value between 0 and 1, put a the point on the spider chart for that percentages
     */
    private getMetricPoint(value: number, type: PsychoAcousticMetricType) {
        const psychoacousticMetric = this.psychoAcousticMetricLayout[type];
        const vector = Point2.Subtract(psychoacousticMetric.maxValuePoint, SpiderChartFactory.SpiderChartCenter);
        const offset = Point2.Multiply(vector, value);

        return Point2.Add(SpiderChartFactory.SpiderChartCenter, offset);
    }

    /**
     * Calculates the points for a regular n-sided polygon (n-gon).
     */
    public static getNGonPoints(n: number, radius: number, center: Point2, rotationRadians = -0.5 * Math.PI): Point2[] {
        const points = [];
        for (let i = 0; i < n; i++) {
            points.push({
                x: center.x + radius * Math.cos((2 * Math.PI * i) / n + rotationRadians),
                y: center.y + radius * Math.sin((2 * Math.PI * i) / n + rotationRadians),
            });
        }
        return points;
    }

    /**
     * Calculates the area of a polygon using the Shoelace formula.
     */
    public calculatePolygonArea(points: { x: number; y: number }[]): number {
        let area = 0;
        const numPoints = points.length;

        for (let i = 0; i < numPoints; i++) {
            const nextIndex = (i + 1) % numPoints;
            area += points[i].x * points[nextIndex].y - points[nextIndex].x * points[i].y;
        }

        return Math.abs(area / 2);
    }

    //#endregion

    //#region Interaction and Event Handling

    /**
     * Highlights a measurement in the spider chart by displaying the created polygon on top.
     * This increases the fill opacity of the polygon and displays the metric values.
     *
     * @param measurement - The measurement to highlight.
     * @param color - The color to use for the highlighted measurement.
     * @param values - The psychoacoustic values corresponding to the measurement.
     */
    public highlightMeasurement(measurement: GenericMeasurement, color: string, values: PsychoAcousticAnalysisResult) {
        if (!measurement) {
            return;
        }

        // Highlight the polygon representing the measurement by adjusting its fill opacity
        const elementId = this.getMeasurementSVGElementId(measurement.id);
        const polygon = document.getElementById(elementId);
        if (polygon) {
            polygon.setAttribute("fill-opacity", SpiderChartFactory.POLYGON_HIGHLIGHT_OPACITY);
        }

        // Clear existing warning icons and display the updated metric values
        this.clearWarningIcons();
        this.displayMetricValues(values, color);
    }

    /**
     * Displays the metric values in the corresponding text elements for the psychoacoustic values.
     * If a value is below 0, it displays a warning icon instead.
     *
     * @param values - The psychoacoustic values for the measurement.
     * @param color - The color to use for the metric text (optional).
     */
    private displayMetricValues(values?: PsychoAcousticAnalysisResult, color?: string) {
        if (!values) return;

        for (const metricType of AllPsychoAcousticMetricTypes) {
            const metric = this.psychoAcousticMetricLayout[metricType];
            const value = values[metricType];

            const metricTspanElementId = this.getTextElementId(metric.unit.unit);
            const metricTspanElement = document.getElementById(metricTspanElementId);

            if (metricTspanElement) {
                if (color) {
                    metricTspanElement.setAttribute("fill", color);
                }

                // Clear the existing text
                metricTspanElement.innerHTML = "";

                if (value < 0 || value == undefined) {
                    this.appendWarningIcon(metricTspanElement);
                } else {
                    // Update with the actual value
                    metricTspanElement.textContent = value.toFixed(2) + " " + metric.unit.unit;
                }
            }
        }
    }

    /**
     * Creates and appends a warning icon to the parent SVG element when a measurement value is too short.
     * The icon is placed near the corresponding metric text element.
     *
     * @param metricTspanElement - The text element used to align the warning icon.
     */
    private appendWarningIcon(metricTspanElement: Element) {
        const parent = metricTspanElement.parentNode?.parentElement;

        const parentX = parseFloat(metricTspanElement.parentElement?.getAttribute("x") || "0");
        const parentY = parseFloat(metricTspanElement.parentElement?.getAttribute("y") || "0");

        const message = "This measurement is too short. Please use a measurement of at least 5 seconds.";

        // Create the foreignObject using SVGElementBuilder
        const foreignObject = new SVGElementBuilder()
            .createElement("foreignObject")
            .setAttribute("x", (parentX - 6).toString())
            .setAttribute("y", (parentY + 16).toString())
            .setAttribute("width", "18")
            .setAttribute("height", "18")
            .build();

        // Create the i element for FontAwesome icon
        const icon = document.createElement("i");
        icon.className = "fa fa-exclamation-triangle color-sorama-orange";
        icon.title = message;
        icon.style.fontSize = "10px";
        foreignObject.appendChild(icon);

        // Appends the foreignObject and the icon to the parent element
        parent.appendChild(foreignObject);
    }

    /**
     * Clears the highlight of a measurement in the spider chart.
     * This resets the fill opacity of the polygon and clears any displayed metric values or warning icons.
     *
     * @param measurement - The measurement to clear the highlight of.
     */
    public clearHighlightMeasurement(measurement: GenericMeasurement) {
        if (!measurement) {
            return;
        }

        const elementId = this.getMeasurementSVGElementId(measurement.id);
        const polygon = document.getElementById(elementId);
        if (polygon) {
            polygon.setAttribute("fill-opacity", SpiderChartFactory.POLYGON_OPACITY);
        }

        // Clear the displayed metric values and any warning icons
        this.clearMetricValues();
        this.clearWarningIcons();
    }

    /**
     * Clears all displayed metric values from the spider chart.
     * This removes the text content from the tspan elements representing each metric.
     */
    private clearMetricValues() {
        const metricsLayout = this.psychoAcousticMetricLayout;
        for (const metricType of AllPsychoAcousticMetricTypes) {
            const metric = metricsLayout[metricType];
            const metricTspanElementId = this.getTextElementId(metric.unit.unit);
            const metricTspanElement = document.getElementById(metricTspanElementId);

            if (metricTspanElement) {
                metricTspanElement.textContent = "";
            }
        }
    }

    /**
     * Clears all warning icons from the SVG element.f
     * This removes any foreignObject elements representing the warning icons.
     */
    private clearWarningIcons() {
        const warningIcons = document.querySelectorAll("foreignObject");
        warningIcons.forEach((icon) => {
            icon.parentNode?.removeChild(icon);
        });
    }

    //#endregion
}
